import { FormEvent, useEffect, useRef, useState } from "react";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";

import { PdfViewer } from "@verse_core/shared_components";
import { SUPPORT_PHONE_NUMBER } from "../constants";
import { useParams } from "react-router-dom";

const IS_SCOPE_OF_SERVICE_LOCKED = gql`
  query IsScopeOfServiceLocked($accessToken: String!) {
    isScopeOfServiceLocked(accessToken: $accessToken)
  }
`;

const SCOPE_OF_SERVICE_DOCUMENT = gql`
  query ScopeOfServiceDocument($accessToken: String!, $patientDob: String!) {
    scopeOfServiceDocument(accessToken: $accessToken, patientDob: $patientDob) {
      isSigned
      isLocked
    }
  }
`;

const SIGN_SCOPE_OF_SERVICE = gql`
  mutation SignScopeOfService($accessToken: String!, $patientDob: String!) {
    signScopeOfService(accessToken: $accessToken, patientDob: $patientDob)
  }
`;

const btnClass =
  "btn btn-lg btn-success btn-block tw-whitespace-normal tw-py-[20px] tw-text-[19px] tw-font-bold sm:tw-py-[8px] sm:tw-text-[10px] sm:tw-font-normal";
const errorClass =
  "tw-mb-[10px] tw-text-[13px] tw-text-red-600 sm:tw-text-[9px]";

/** Format up to 8 digits as MM/DD/YYYY for display, e.g. "08121950" -> "08/12/1950". */
const formatDob = (digits: string) => {
  const d = digits.slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)]
    .filter(Boolean)
    .join("/");
};

export default function ScopeOfServicePage() {
  const { accessToken } = useParams<{ accessToken: string }>();
  const [dob, setDob] = useState("");
  const [verified, setVerified] = useState(false);
  const [signed, setSigned] = useState(false);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState("");

  useQuery(IS_SCOPE_OF_SERVICE_LOCKED, {
    variables: { accessToken },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.isScopeOfServiceLocked) {
        setLocked(true);
      }
    },
  });
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [pdfWidth, setPdfWidth] = useState(600);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (pdfContainerRef.current) {
        setPdfWidth(Math.max(pdfContainerRef.current.clientWidth - 40, 200));
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [verified]);

  const loadPdf = async (dobValue: string) => {
    if (!accessToken) return;
    const res = await fetch(`/scope-of-service-document/${accessToken}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dob: dobValue }),
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const oldUrl = documentUrl;
    setDocumentUrl(URL.createObjectURL(blob));
    if (oldUrl) URL.revokeObjectURL(oldUrl);
  };

  const [fetchDocument, { loading }] = useLazyQuery(SCOPE_OF_SERVICE_DOCUMENT, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const doc = data?.scopeOfServiceDocument;
      if (!doc) {
        setError("Date of birth does not match our records.");
        return;
      }
      if (doc.isLocked) {
        setLocked(true);
        return;
      }
      setVerified(true);
      setError("");
      if (doc.isSigned) {
        setSigned(true);
      } else {
        loadPdf(dob);
      }
    },
    onError: () => {
      setError("Something went wrong. Please try again.");
    },
  });

  const [signDocument, { loading: signing }] = useMutation(
    SIGN_SCOPE_OF_SERVICE,
    {
      onCompleted: (data) => {
        if (data?.signScopeOfService) {
          setSigned(true);
        }
      },
      onError: () => {
        setError("Failed to sign the document. Please try again.");
      },
    },
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    fetchDocument({
      variables: { accessToken, patientDob: dob },
    });
  };

  const handleSign = () => {
    signDocument({
      variables: { accessToken, patientDob: dob },
    });
  };

  if (locked) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-p-[10px]">
        <div className="tw-w-full tw-max-w-md tw-text-center">
          <div className="tw-mb-[15px] tw-text-[80px] tw-text-red-600 sm:tw-mb-[10px] sm:tw-text-[38px]">
            &#10007;
          </div>
          <h1 className="tw-mb-[10px] tw-text-[30px] tw-font-bold sm:tw-mb-[5px] sm:tw-text-[19px]">
            Document Locked
          </h1>
          <p className="tw-text-[15px] tw-text-gray-600 sm:tw-text-[11px]">
            This document has been locked due to too many failed verification
            attempts. For assistance, please call us at{" "}
            <a
              href={`tel:${SUPPORT_PHONE_NUMBER}`}
              className="tw-text-blue-600 tw-underline"
            >
              {SUPPORT_PHONE_NUMBER}
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  if (verified && signed) {
    return (
      <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-p-[10px]">
        <div className="tw-text-center">
          <div
            className="tw-text-green-600 tw-mb-[20px]"
            style={{ fontSize: "120px" }}
          >
            &#10003;
          </div>
          <h1 className="tw-mb-[15px] tw-text-[45px] tw-font-bold sm:tw-mb-[5px] sm:tw-text-[19px]">
            Signed!
          </h1>
          <p className="tw-text-[23px] tw-text-gray-600 sm:tw-text-[11px]">
            Your document has been signed. You may close this page.
          </p>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="tw-flex tw-min-h-screen tw-flex-col tw-items-center">
        <div className="tw-w-full tw-max-w-4xl tw-flex-1 tw-px-[8px] tw-pb-[70px] tw-pt-[10px] sm:tw-px-[15px] sm:tw-pt-[15px]">
          <h1 className="tw-mb-[10px] tw-text-[15px] tw-font-bold">
            Scope of Service
          </h1>
          {error && <div className={errorClass}>{error}</div>}
          <div ref={pdfContainerRef}>
            {documentUrl ? (
              <PdfViewer file={documentUrl} pageWidth={pdfWidth} />
            ) : (
              <p className="tw-mb-[10px] tw-text-gray-500">
                Loading document...
              </p>
            )}
          </div>
        </div>
        <div className="tw-fixed tw-inset-x-0 tw-bottom-0 tw-border-t tw-border-gray-200 tw-bg-white tw-p-[10px] tw-shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
          <div className="tw-mx-auto tw-max-w-4xl">
            <button
              type="button"
              className={btnClass}
              disabled={signing}
              onClick={handleSign}
            >
              {signing ? "Signing..." : "Sign to Acknowledge Package Receipt"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-p-[10px]">
      <div className="tw-w-full tw-max-w-lg">
        <div className="tw-rounded-[3px] tw-border tw-border-gray-200 tw-bg-white tw-p-[20px] tw-shadow sm:tw-p-[15px]">
          <h2 className="tw-mb-[15px] tw-text-[30px] tw-font-bold sm:tw-mb-[10px] sm:tw-text-[13px]">
            Verify Your Identity
          </h2>
          <p className="tw-mb-[20px] tw-text-[19px] tw-text-gray-600 sm:tw-mb-[10px] sm:tw-text-[10px]">
            Please enter your date of birth to continue.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="tw-mb-[20px]">
              <label
                htmlFor="dob"
                className="tw-mb-[8px] tw-block tw-text-[19px] tw-font-medium tw-text-gray-700 sm:tw-text-[10px]"
              >
                Date of Birth
              </label>
              <input
                id="dob"
                type="text"
                inputMode="numeric"
                className="form-control tw-py-[20px] tw-text-[38px] sm:tw-py-[5px] sm:tw-text-[10px]"
                placeholder="MM/DD/YYYY"
                value={formatDob(dob)}
                onChange={(e) =>
                  setDob(e.target.value.replace(/\D/g, "").slice(0, 8))
                }
              />
              <p className="tw-mt-[6px] tw-text-[15px] tw-text-gray-500 sm:tw-text-[9px]">
                Please enter your full date of birth, including the 4-digit year
                (MM/DD/YYYY).
              </p>
            </div>
            {error && <div className={errorClass}>{error}</div>}
            <button
              type="submit"
              className={btnClass}
              disabled={loading || dob.length !== 8}
            >
              {loading ? "Verifying..." : "Continue"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
